import { Link } from "react-router";
import {
  formatCrmTaskChecklistSummary,
  formatCrmTaskPlaybookSummary,
  formatCrmTaskProfileSectionSummary,
  getCrmTaskChecklistSummaryToneClasses,
  getCrmTaskPlaybookToneClasses,
  getCrmTaskProfileSectionSummaryToneClasses,
} from "@/components/crm/task-guidance-panels";
import { cn } from "@/lib/utils";
import type { CrmTaskContextCompletenessStatus } from "@/components/crm/task-detail-state-cards";
import type { CrmTaskExecutionChecklistSummary } from "@ease/crm/task-execution-checklist";
import type { CrmTaskProfileSectionSummary } from "@ease/crm/task-profile-sections";
import type { CrmTaskPlaybookSummary } from "@ease/crm/task-playbooks";

export interface CrmTaskRowDueDisplay {
  label: string;
  tone: "default" | "info" | "warning" | "critical";
}

export interface CrmTaskRowRecordLink {
  href: string;
  label: string;
}

interface CrmTaskRowFactsProps {
  priority: string;
  priorityClassName: string | undefined;
  typeLabel: string | null;
  sourceLabel: string | null;
  automationResolved: boolean;
  automationWatching: boolean;
  slaLabel: string | null;
  slaTone: "default" | "info" | "warning" | "critical" | null;
  slaDetail: string | null;
  contextCompletenessStatus: CrmTaskContextCompletenessStatus | null;
  profileSectionSummary: CrmTaskProfileSectionSummary | null;
  playbookSummary: CrmTaskPlaybookSummary | null;
  checklistSummary: CrmTaskExecutionChecklistSummary | null;
  dueDisplay: CrmTaskRowDueDisplay | null;
  assigneeLabel: string | null;
  recurrenceLabel: string | null;
  contactLink: CrmTaskRowRecordLink | null;
  dealLink: CrmTaskRowRecordLink | null;
  companyLink: CrmTaskRowRecordLink | null;
  offerLinked: boolean;
  formatLabel: (value: string) => string | null;
}

export function CrmTaskRowFacts({
  priority,
  priorityClassName,
  typeLabel,
  sourceLabel,
  automationResolved,
  automationWatching,
  slaLabel,
  slaTone,
  slaDetail,
  contextCompletenessStatus,
  profileSectionSummary,
  playbookSummary,
  checklistSummary,
  dueDisplay,
  assigneeLabel,
  recurrenceLabel,
  contactLink,
  dealLink,
  companyLink,
  offerLinked,
  formatLabel,
}: CrmTaskRowFactsProps) {
  return (
    <div className="mt-2 flex flex-wrap gap-2 text-xs text-[var(--color-muted-foreground)]">
      <span className={cn("font-medium", priorityClassName)}>
        <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-current align-middle" />
        {formatLabel(priority)}
      </span>
      {typeLabel && <span>{typeLabel}</span>}
      {sourceLabel && <span>Source: {sourceLabel}</span>}
      {automationResolved && (
        <span className="rounded-full border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 px-2 py-0.5 font-medium text-[var(--color-success)]">
          Auto-closed
        </span>
      )}
      {automationWatching && !automationResolved && (
        <span className="rounded-full border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 px-2 py-0.5 font-medium text-[var(--color-primary)]">
          Auto-resolution armed
        </span>
      )}
      {slaLabel && (
        <span className={cn(
          "rounded-full border px-2 py-0.5 font-medium",
          slaTone === "critical"
            ? "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]"
            : slaTone === "warning"
              ? "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
              : slaTone === "info"
                ? "border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-muted-foreground)]",
        )}>
          {slaLabel}{slaDetail ? ` · ${slaDetail}` : ""}
        </span>
      )}
      {contextCompletenessStatus && (
        <span className={cn(
          "rounded-full border px-2 py-0.5 font-medium",
          contextCompletenessStatus === "ready"
            ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]"
            : contextCompletenessStatus === "partial"
              ? "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
              : "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
        )}>
          {contextCompletenessStatus === "ready"
            ? "Context ready"
            : contextCompletenessStatus === "partial"
              ? "Context partial"
              : "Needs operator context"}
        </span>
      )}
      {profileSectionSummary && (
        <span className={cn(
          "rounded-full border px-2 py-0.5 font-medium",
          getCrmTaskProfileSectionSummaryToneClasses(profileSectionSummary),
        )}>
          {formatCrmTaskProfileSectionSummary(profileSectionSummary)}
        </span>
      )}
      {playbookSummary && (
        <span className={cn(
          "rounded-full border px-2 py-0.5 font-medium",
          getCrmTaskPlaybookToneClasses(playbookSummary.tone),
        )}>
          {formatCrmTaskPlaybookSummary(playbookSummary)}
        </span>
      )}
      {checklistSummary && (
        <span className={cn(
          "rounded-full border px-2 py-0.5 font-medium",
          getCrmTaskChecklistSummaryToneClasses(checklistSummary),
        )}>
          {formatCrmTaskChecklistSummary(checklistSummary)}
        </span>
      )}
      {dueDisplay && (
        <span className={cn(
          dueDisplay.tone === "critical" && "font-medium text-[var(--color-destructive)]",
          dueDisplay.tone === "warning" && "font-medium text-[var(--color-warning)]",
          dueDisplay.tone === "info" && "font-medium text-[var(--color-primary)]",
        )}>
          {dueDisplay.label}
        </span>
      )}
      {assigneeLabel && <span>Assigned: {assigneeLabel}</span>}
      {recurrenceLabel && <span>{recurrenceLabel} recurring</span>}
      {contactLink && <Link to={contactLink.href} className="underline">{contactLink.label}</Link>}
      {dealLink && <Link to={dealLink.href} className="underline">{dealLink.label}</Link>}
      {companyLink && <Link to={companyLink.href} className="underline">{companyLink.label}</Link>}
      {offerLinked && <span>Offer linked</span>}
    </div>
  );
}
